import { useEffect, useState } from 'react';
import { safeRequestIdleCallback } from '../helpers/utils';

/**
 * Custom hook for deferred font loading
 * Loads fonts after the page has loaded to avoid blocking initial render
 */
export const useFonts = () => {
    const [fontsLoaded, setFontsLoaded] = useState(false);

    useEffect(() => {
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => {
                setFontsLoaded(true);
            });
        } else {
            setFontsLoaded(true);
        }
    }, []);

    return fontsLoaded;
};

/**
 * Load optimized fonts asynchronously with performance optimization
 * This function loads non-critical fonts after initial render
 */
export const loadFonts = () => {
    return new Promise(resolve => {
        const loadFontsWhenIdle = () => {
            // CRITICAL: Load Gilroy-Regular first (needed for subtitle LCP)
            const criticalFonts = ['/fonts/Gilroy-Regular.woff2'];
            const nonCriticalFonts = ['/fonts/Gilroy-Medium.woff2','/fonts/Gilroy-SemiBold.woff2','/fonts/Gilroy-Bold.woff2'];
            
            // Load critical font immediately
            criticalFonts.forEach((href, i) => {
                const l = document.createElement('link');
                l.rel = 'preload';
                l.as = 'font';
                l.href = href;
                l.type = 'font/woff2';
                l.crossOrigin = 'anonymous';
                document.head.appendChild(l);
            });
            
            // Load non-critical fonts with delay
            nonCriticalFonts.forEach((href, i) => {
                setTimeout(() => {
                    const l = document.createElement('link');
                    l.rel = 'preload';
                    l.as = 'font';
                    l.href = href;
                    l.type = 'font/woff2';
                    l.crossOrigin = 'anonymous';
                    document.head.appendChild(l);
                }, (i + 1) * 200);
            });
            
            resolve();
            // // Load optimized font CSS with WOFF2 fonts
            // const link = document.createElement('link');
            // link.rel = 'stylesheet';
            // link.href = '/fonts/gilroy-optimized.css';
            // link.crossOrigin = 'anonymous';

            // document.head.appendChild(link);

            // // Progressively load non-critical font weights
            // const nonCriticalFonts = [
            //     '/fonts/Gilroy-Medium.woff2',
            //     '/fonts/Gilroy-SemiBold.woff2', 
            //     '/fonts/Gilroy-Bold.woff2'
            // ];

            // // Load each font with staggered timing
            // nonCriticalFonts.forEach((fontUrl, index) => {
            //     setTimeout(() => {
            //         const fontLink = document.createElement('link');
            //         fontLink.rel = 'preload';
            //         fontLink.href = fontUrl;
            //         fontLink.as = 'font';
            //         fontLink.type = 'font/woff2';
            //         fontLink.crossOrigin = 'anonymous';
            //         document.head.appendChild(fontLink);
            //     }, index * 200); // Stagger loading by 200ms
            // });

            // link.onload = () => resolve();
            // link.onerror = () => resolve();
        };

        safeRequestIdleCallback(loadFontsWhenIdle, { timeout: 2000 });
    });
};

/**
 * Preload critical fonts with performance optimization
 * Call this in _document.js or _app.js for critical fonts
 */
export const preloadCriticalFonts = () => {
    const criticalFonts = ['/fonts/Gilroy-Regular.woff2'];

    const preloadFonts = () => {
        const loadFontSequentially = index => {
            if (index >= criticalFonts.length) return;

            const fontPath = criticalFonts[index];
            const link = document.createElement('link');
            link.rel = 'preload';
            link.href = fontPath;
            link.as = 'font';
            link.type = 'font/woff2';
            link.crossOrigin = 'anonymous';
            document.head.appendChild(link);

            setTimeout(() => {
                loadFontSequentially(index + 1);
            }, 50);
        };

        loadFontSequentially(0);
    };

    safeRequestIdleCallback(preloadFonts, { timeout: 500 });
};

/**
 * Progressive font loading - loads fonts in stages to improve performance
 * Uses chunking to avoid long main-thread tasks
 */
export const loadFontsProgressively = () => {
    const fontStages = [
        { fonts: ['/fonts/Gilroy-Regular.woff2'], delay: 0 },
        { fonts: ['/fonts/Gilroy-Medium.woff2'], delay: 500 },
        { fonts: ['/fonts/Gilroy-SemiBold.woff2'], delay: 1000 },
        { fonts: ['/fonts/Gilroy-Bold.woff2'], delay: 1500 }
    ];

    const loadFontChunk = (stageIndex, fontIndex) => {
        if (stageIndex >= fontStages.length) return;

        const stage = fontStages[stageIndex];
        if (fontIndex >= stage.fonts.length) {
            setTimeout(() => {
                loadFontChunk(stageIndex + 1, 0);
            }, stage.delay);
            return;
        }

        const fontPath = stage.fonts[fontIndex];
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = fontPath;
        link.as = 'font';
        link.type = 'font/woff2';
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);

        setTimeout(() => {
            loadFontChunk(stageIndex, fontIndex + 1);
        }, 100);
    };

    safeRequestIdleCallback(
        () => {
            loadFontChunk(0, 0);
        },
        { timeout: 1000 }
    );
};
